/**
 * TanStack Query client configuration
 *
 * Design decisions (per FRONTEND.md §4):
 * - Money mutations are pessimistic — never optimistic.
 * - Approve/reject are optimistic with rollback on failure.
 * - refetchOnWindowFocus: true (live-ish data)
 * - 30s stale time as baseline; Dashboard/Approvals poll additionally.
 */

import { QueryClient } from '@tanstack/react-query';
import { classifyApiError } from '@budgeting101/api-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:          30_000,       // 30 s — matches polling interval
      gcTime:             5 * 60_000,   // 5 min garbage-collect
      // Retry only failures that retrying can fix. Permission, validation,
      // auth, and domain conflicts are answers, not glitches.
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        const kind = classifyApiError(error);
        return kind === 'network' || kind === 'server' || kind === 'unknown';
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      // Global fallback for failures without contextual handling. Typed 4xx
      // outcomes (validation, permission, conflicts) stay with their callers.
      onError: (error: unknown) => {
        const kind = classifyApiError(error);
        if (kind === 'offline' || kind === 'network' || kind === 'server' || kind === 'unknown') {
          window.dispatchEvent(
            new CustomEvent('query:server-error', { detail: error }),
          );
        }
      },
    },
  },
});
